import { c as _c } from "react-compiler-runtime";
import { useCellSelectionDispatch, useCellSelectionSelector, useCellSelectionStore } from '../../redux/reduxHooks';
import { isMacOs } from '../utils/isMacOs';
import { selectCell, addRange, extendActiveRange, dragOver, startFillDrag, getHasSelection, isOnlySelectedCell, isSameCoord, getCellSelectionState } from '../redux/cellSelectionSlice';
import { useTrackCellSelection } from '../context/CellSelectionContext';
import { isEventFromOverlay } from '../utils/isEventFromOverlay';

// Hit-test size (px) for the fill handle, measured inward from the cell's
// inner bottom-right corner (rect.right / rect.bottom). Intentionally larger
// than the visible 8×8px dot (::after in StyledTableComponents) to make the
// small drag target easier to grab.
const FILL_HANDLE_HIT_SIZE = 12;
const isWithinFillHandle = e => {
  const rect = e.currentTarget.getBoundingClientRect();
  return e.clientX >= rect.right - FILL_HANDLE_HIT_SIZE && e.clientY >= rect.bottom - FILL_HANDLE_HIT_SIZE;
};

/**
 * Builds the mousedown/mouseenter handlers for a single cell at
 * `{ rowIndex, columnIndex }`. Returned object is spread onto the cell's
 * `requiredCellProps` (and from there onto the consumer's `<td>`).
 *
 * - mousedown maps the modifier keys to the three spreadsheet gestures:
 *   shift extends the active range, Cmd/Ctrl adds a new disjoint range (or
 *   deselects the cell if it is already selected), and a plain click starts a
 *   fresh single-cell selection.
 * - mouseenter extends the active range while a drag is in progress (the
 *   reducer no-ops otherwise).
 *
 * `preventDefault` on mousedown is deliberately limited to the explicit
 * selection gestures (fill-handle drag, Shift-click, Cmd/Ctrl-click). A plain
 * mousedown must NOT preventDefault, otherwise the browser never starts a
 * native text selection and users cannot click-and-drag to highlight text
 * inside a cell. Native text selection is suppressed later, only once a drag
 * actually crosses into another cell (see
 * useSuppressTextSelectionWhileCellDrag).
 *
 * Events that originate inside an overlay portalled from the cell (a popover,
 * dropdown, dialog) are ignored: React routes synthetic events up the React
 * tree, so they reach the cell even though the overlay is a sibling of <body>
 * in the DOM. Acting on them would preventDefault the mousedown and stop the
 * overlay's own inputs from taking focus.
 */
export const useCellSelectionHandlers = (rowIndex, columnIndex, isFillHandleCell) => {
  const $ = _c(16);
  const dispatch = useCellSelectionDispatch();
  const store = useCellSelectionStore();
  const track = useTrackCellSelection();
  const hasSelection = useCellSelectionSelector(getHasSelection);
  let t0;
  if ($[0] !== columnIndex || $[1] !== rowIndex) {
    t0 = {
      rowIndex,
      columnIndex
    };
    $[0] = columnIndex;
    $[1] = rowIndex;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const coord = t0;
  let t1;
  if ($[3] !== coord || $[4] !== dispatch || $[5] !== hasSelection || $[6] !== isFillHandleCell || $[7] !== store || $[8] !== track) {
    t1 = e => {
      if (e.button !== 0) {
        return;
      }
      if (isEventFromOverlay(e.target, e.currentTarget)) {
        return;
      }
      if (isFillHandleCell && isWithinFillHandle(e)) {
        e.preventDefault();
        dispatch(startFillDrag());
        return;
      }
      if (e.shiftKey) {
        e.preventDefault();
        const ranges = getCellSelectionState(store.getState()).ranges;
        const active = ranges[ranges.length - 1];
        const isNoop = !!active && isSameCoord(active.focus, coord);
        if (!isNoop) {
          track === null || track === void 0 || track({
            type: hasSelection ? "cellSelectionChanged" : "cellSelectionStarted",
            method: "shiftClick"
          });
        }
        dispatch(extendActiveRange(coord));
        return;
      }
      if (isMacOs() ? e.metaKey : e.ctrlKey) {
        e.preventDefault();
        const isDeselecting = isOnlySelectedCell(getCellSelectionState(store.getState()), coord);
        if (!isDeselecting) {
          track === null || track === void 0 || track({
            type: hasSelection ? "cellSelectionChanged" : "cellSelectionStarted",
            method: "cmdClick"
          });
        }
        dispatch(addRange(coord));
        return;
      }
      if (e.currentTarget instanceof HTMLElement && e.currentTarget.getAttribute("data-editing") === "true") {
        return;
      }
      const isDeselecting_0 = isOnlySelectedCell(getCellSelectionState(store.getState()), coord);
      if (!isDeselecting_0) {
        track === null || track === void 0 || track({
          type: "cellSelectionStarted",
          method: "plainClick"
        });
      }
      dispatch(selectCell(coord));
    };
    $[3] = coord;
    $[4] = dispatch;
    $[5] = hasSelection;
    $[6] = isFillHandleCell;
    $[7] = store;
    $[8] = track;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== coord || $[11] !== dispatch) {
    t2 = e_0 => {
      const isLeftButtonDown = (e_0.buttons & 1) !== 0;
      if (isLeftButtonDown) {
        dispatch(dragOver(coord));
      }
    };
    $[10] = coord;
    $[11] = dispatch;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== t1 || $[14] !== t2) {
    t3 = {
      onMouseDown: t1,
      onMouseEnter: t2
    };
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
};